import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Trash04Icon } from '@langchain/untitled-ui-icons';

import { useCallback, useRef, useState } from 'react';
import { mutate as mutateGlobal } from 'swr';

import { gatherSecrets } from '@/Pages/Playground/components/PlaygroundSecrets.utils';
import { EvaluatorSecrets } from '@/Pages/Settings/components/EvaluatorSecrets';
import { useTenantSecretSet } from '@/Pages/Settings/utils/useTenantSecretSet';
import { FetcherParams } from '@/data/fetcher';
import { Button, Tooltip } from '@/design-system';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import {
  DatasetRuleMutateBody,
  EvaluatorRuleSchema,
  RuleSchema,
  StructuredEvaluatorSchema,
} from '@/types/schema';
import { apiRunsPath } from '@/utils/constants';

import { DeleteModal } from '../Delete';
import useToast from '../Toast/useToast';
import { useEvaluatorStore } from './store/evaluatorStore';
import { reinsertFewShotToSerializedManifest } from './utils/fewShotExamplesUtils';
import { useCommitAndSaveRule } from './utils/useCommitAndSaveRule';

export function DatasetEvaluatorCrudPaneHeader({
  datasetId,

  onClose,
  onSuccess: _onSuccess,
  selectedRule,
  onDiscard,
}: {
  datasetId: string;

  onClose: () => void;
  onSuccess: (ruleId: string) => void;
  selectedRule?: EvaluatorRuleSchema | DatasetRuleMutateBody;
  onDiscard: () => void;
}) {
  const ruleId =
    selectedRule && 'id' in selectedRule ? selectedRule.id : undefined;

  const [deleteRuleId, setDeleteRuleId] = useState<string | undefined>(
    undefined
  );

  const edited = useEvaluatorStore((state) => state.edited);
  const exportEvaluator = useEvaluatorStore((state) => state.exportEvaluator);
  const model = useEvaluatorStore((state) => state.model);

  const fewShotEnabled = useEvaluatorStore((state) => state.fewShotEnabled);
  const fewShotContent = useEvaluatorStore((state) => state.fewShotContent);

  const openSecretsRef = useRef<(() => void) | null>(null);
  const secretKeys = useTenantSecretSet();

  const { createToast } = useToast();
  const bypassAuth = useInstanceFlag(
    InstanceFlags.playground_auth_bypass_enabled
  );

  const hubRefManifest = useEvaluatorStore((state) => state.hubRefManifest);

  const transformManifest = useCallback(
    (manifest: SerializedConstructor) => {
      if (
        fewShotEnabled &&
        fewShotContent &&
        manifest?.kwargs?.first?.kwargs?.messages
      ) {
        return reinsertFewShotToSerializedManifest(manifest, fewShotContent);
      }
      return manifest;
    },
    [fewShotEnabled, fewShotContent]
  );

  const onSuccess = useCallback(
    async (newRule: RuleSchema) => {
      if (!newRule) {
        return;
      }
      await mutateGlobal((key: FetcherParams) => key?.url?.includes('commits'));
      _onSuccess(newRule.id);
    },
    [_onSuccess]
  );

  const { saveRule, isSavingEvaluator } = useCommitAndSaveRule({
    datasetId,
    existingRule: selectedRule,
    transformManifest,
    onSuccess,
    hubRefManifest,
  });

  const handleSave = async () => {
    if (gatherSecrets(model).some((i) => !secretKeys.has(i)) && !bypassAuth) {
      openSecretsRef.current?.();
      return;
    }
    try {
      const validatedEvaluator = exportEvaluator();
      if (!validatedEvaluator) {
        return;
      }
      await saveRule({ validatedEvaluator });
    } catch (error) {
      createToast({
        title: 'Error',
        description: `Failed to save evaluator: ${error}`,
        type: 'error',
      });
    }
  };

  return (
    <>
      <EvaluatorCrudPanelHeader
        edited={edited}
        onSave={handleSave}
        isSaving={isSavingEvaluator}
        onDiscard={onDiscard}
        onDelete={() => setDeleteRuleId(ruleId)}
        selectedEvaluator={selectedRule?.evaluators?.[0].structured}
        secretsRef={openSecretsRef}
      />

      {deleteRuleId && (
        <DeleteModal
          endpoint={`${apiRunsPath}/rules`}
          id={deleteRuleId}
          isOpen={!!deleteRuleId}
          doClose={() => {
            setDeleteRuleId(undefined);
          }}
          onSuccess={() => {
            setDeleteRuleId(undefined);
            onClose();
          }}
        />
      )}
    </>
  );
}

export function SessionsEvaluatorCrudPaneHeader({
  onClose,
  onSave,
  isSaving,
  selectedEvaluator,
  saveDisabled,
}: {
  onClose: () => void;
  onSave: (evaluator: StructuredEvaluatorSchema) => void;
  isSaving?: boolean;
  selectedEvaluator?: StructuredEvaluatorSchema;
  saveDisabled?: boolean;
}) {
  const reset = useEvaluatorStore((state) => state.reset);
  const exportEvaluator = useEvaluatorStore((state) => state.exportEvaluator);
  const edited = useEvaluatorStore((state) => state.edited);

  const { createToast } = useToast();

  const handleDiscard = () => {
    reset();
    onClose();
  };

  const model = useEvaluatorStore((state) => state.model);
  const openSecretsRef = useRef<(() => void) | null>(null);
  const secretKeys = useTenantSecretSet();

  const handleSave = async () => {
    if (gatherSecrets(model).some((i) => !secretKeys.has(i))) {
      openSecretsRef.current?.();
      return;
    }
    try {
      const validatedEvaluator = exportEvaluator();
      if (!validatedEvaluator) {
        return;
      }
      await onSave(validatedEvaluator);
    } catch (error) {
      createToast({
        title: 'Error',
        description: `Failed to save evaluator: ${error}`,
        type: 'error',
      });
    }
  };
  return (
    <EvaluatorCrudPanelHeader
      edited={edited}
      onSave={handleSave}
      onDiscard={handleDiscard}
      selectedEvaluator={selectedEvaluator}
      secretsRef={openSecretsRef}
      saveDisabled={saveDisabled}
      isSaving={isSaving}
    />
  );
}

function EvaluatorCrudPanelHeader({
  edited,
  onSave,
  isSaving,

  selectedEvaluator,
  onDiscard,
  onDelete,
  secretsRef,
  saveDisabled,
}: {
  selectedEvaluator?: StructuredEvaluatorSchema;

  edited: boolean;
  onDiscard: () => void;

  onSave: () => void;
  isSaving?: boolean;
  saveDisabled?: boolean;

  onDelete?: () => void;

  secretsRef?: React.RefObject<(() => void) | null>;
}) {
  const model = useEvaluatorStore((state) => state.model);

  return (
    <div className="flex flex-row gap-2 px-4">
      {selectedEvaluator && onDelete && (
        <Button
          color="error"
          variant="outlined"
          size="md"
          leftDecorator={Trash04Icon}
          onClick={() => {
            onDelete();
          }}
        >
          Delete Evaluator
        </Button>
      )}
      {edited && onDiscard && (
        <Button
          color="secondary"
          variant="outlined"
          size="md"
          onClick={onDiscard}
        >
          Discard changes
        </Button>
      )}
      <Tooltip
        title={
          edited
            ? 'Save changes'
            : saveDisabled
            ? 'Rule is invalid'
            : 'No changes'
        }
      >
        <div>
          <Button
            size="md"
            onClick={onSave}
            loading={isSaving}
            disabled={isSaving || saveDisabled}
          >
            {selectedEvaluator ? 'Save' : 'Create'}
          </Button>
        </div>
      </Tooltip>
      {secretsRef && (
        <EvaluatorSecrets
          model={model ?? undefined}
          openSecretsRef={secretsRef}
          infoMessage={
            'Your API keys are stored encrypted on our servers in order to invoke evaluators without attendance.'
          }
          triggerComponent={<div className="h-8 w-1" />}
        />
      )}
    </div>
  );
}
